import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import Icon from '../../components/Icon';

export default function LORCommissioner({ api }) {
  const { currentUser } = useUser();
  const isCommissioner =
    currentUser?.display_name === 'PhillyxCommish' ||
    currentUser?.username === 'PhillyxCommish';

  const [pass, setPass] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [tab, setTab] = useState('seasons');

  // Keeper HQ state
  const [keeperBoard, setKeeperBoard]         = useState(null);
  const [keeperOverrides, setKeeperOverrides] = useState([]);
  const [keeperAdpList, setKeeperAdpList]     = useState([]);
  const [keeperMsg, setKeeperMsg]             = useState(null);
  const [adpForm, setAdpForm]                 = useState({ player_id: '', player_name: '', adp_round: '', notes: '' });
  const [overrideForm, setOverrideForm]       = useState({ player_id: '', player_name: '', eligible: true, corrected_draft_round: '', reason: '' });
  const [allRecs, setAllRecs]                 = useState(null);
  const [allRecsLoading, setAllRecsLoading]   = useState(false);

  // Season entry state
  const [seasons, setSeasons] = useState([]);
  const [seasonForm, setSeasonForm] = useState({
    season: new Date().getFullYear() - 1,
    champion_name: '',
    runner_up_name: '',
    source: 'manual',
    notes: '',
  });
  const [seasonSaving, setSeasonSaving] = useState(false);
  const [seasonMsg, setSeasonMsg] = useState(null);

  // Manager state
  const [managers, setManagers] = useState([]);

  // Audit log
  const [auditLog, setAuditLog] = useState([]);

  // Lore state
  const [loreOverview, setLoreOverview]     = useState(null);
  const [loreLoading, setLoreLoading]       = useState(false);
  const [loreMsg, setLoreMsg]               = useState(null);
  const [aiGenerating, setAiGenerating]     = useState(false);
  const [aiResult, setAiResult]             = useState('');
  const [mockDrafts,      setMockDrafts]      = useState([]);
  const [mockDraftsLoading, setMockDraftsLoading] = useState(false);
  const [mockDraftDetail,  setMockDraftDetail]  = useState(null);
  const [mockFilter,       setMockFilter]      = useState('');  // filter by manager name

  if (!isCommissioner) {
    return (
      <div className="lor-empty">
        <div style={{ marginBottom: 12 }}><Icon emoji="🔒" size={28} /></div>
        Commissioner access only.
      </div>
    );
  }

  const doAuth = async () => {
    setAuthError('');
    try {
      const r = await fetch(`${api}/api/commissioner/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': pass },
        body: JSON.stringify({}),
      });
      if (r.ok) {
        setAuthed(true);
        loadAll();
      } else {
        setAuthError('Incorrect password.');
      }
    } catch {
      setAuthError('Connection error.');
    }
  };

  const loadAll = async () => {
    const [s, m, a] = await Promise.all([
      fetch(`${api}/api/lor/commissioner/seasons`, { headers: { 'X-HQ-Pass': pass } })
        .then(r => r.json()).catch(() => []),
      fetch(`${api}/api/lor/commissioner/managers`, { headers: { 'X-HQ-Pass': pass } })
        .then(r => r.json()).catch(() => []),
      fetch(`${api}/api/lor/commissioner/audit`, { headers: { 'X-HQ-Pass': pass } })
        .then(r => r.json()).catch(() => []),
    ]);
    setSeasons(s);
    setManagers(m);
    setAuditLog(a);
  };

  const loadKeeperData = async () => {
    const [board, overrides, adpList] = await Promise.all([
      fetch(`${api}/api/lor/commissioner/keepers`, { headers: { 'X-HQ-Pass': pass } })
        .then(r => r.json()).catch(() => null),
      fetch(`${api}/api/lor/commissioner/keeper-overrides`, { headers: { 'X-HQ-Pass': pass } })
        .then(r => r.json()).catch(() => []),
      fetch(`${api}/api/lor/commissioner/keeper-adp-list`, { headers: { 'X-HQ-Pass': pass } })
        .then(r => r.json()).catch(() => []),
    ]);
    setKeeperBoard(board);
    setKeeperOverrides(overrides);
    setKeeperAdpList(adpList);
  };

  const markDeclarationReceived = async (roster_id, manager_name) => {
    setKeeperMsg('');
    const r = await fetch(`${api}/api/lor/commissioner/keeper-declaration/${roster_id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': pass },
      body: JSON.stringify({ status: 'received', declared_players: [], notes: null }),
    });
    if (r.ok) { setKeeperMsg({ type: 'success', text: `${manager_name} marked as received.` }); loadKeeperData(); }
    else setKeeperMsg({ type: 'error', text: 'Failed to update declaration status.' });
  };

  const saveAdpOverride = async () => {
    if (!adpForm.player_id || !adpForm.adp_round) { setKeeperMsg({ type: 'error', text: 'Player ID and ADP round are required.' }); return; }
    const r = await fetch(`${api}/api/lor/commissioner/keeper-adp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': pass },
      body: JSON.stringify({ ...adpForm, adp_round: parseInt(adpForm.adp_round) }),
    });
    if (r.ok) {
      setKeeperMsg({ type: 'success', text: 'ADP override saved.' });
      setAdpForm({ player_id: '', player_name: '', adp_round: '', notes: '' });
      loadKeeperData();
    } else setKeeperMsg({ type: 'error', text: 'Failed to save ADP override.' });
  };

  const saveEligibilityOverride = async () => {
    if (!overrideForm.player_id || !overrideForm.reason) { setKeeperMsg({ type: 'error', text: 'Player ID and reason are required.' }); return; }
    const r = await fetch(`${api}/api/lor/commissioner/keeper-override`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': pass },
      body: JSON.stringify({
        ...overrideForm,
        corrected_draft_round: overrideForm.corrected_draft_round
          ? parseInt(overrideForm.corrected_draft_round, 10)
          : null,
      }),
    });
    if (r.ok) {
      setKeeperMsg({ type: 'success', text: 'Eligibility override saved.' });
      setOverrideForm({ player_id: '', player_name: '', eligible: true, corrected_draft_round: '', reason: '' });
      loadKeeperData();
    } else {
      const error = await r.json().catch(() => ({}));
      setKeeperMsg({ type: 'error', text: error.detail || 'Failed to save override.' });
    }
  };

  const postKeeperBoard = async () => {
    if (!window.confirm('Post the full keeper board to #league-of-record-intel?')) return;
    const r = await fetch(`${api}/api/lor/discord/keeper-board`, {
      method: 'POST', headers: { 'X-HQ-Pass': pass },
    });
    const d = await r.json();
    alert(d.posted ? '✅ Keeper board posted!' : '❌ Failed to post. Check Discord config.');
  };

  const postDeadlineReminder = async (days) => {
    if (!window.confirm(`Post a ${days}-day keeper deadline reminder to Discord?`)) return;
    const r = await fetch(`${api}/api/lor/discord/keeper-deadline?days_left=${days}`, {
      method: 'POST', headers: { 'X-HQ-Pass': pass },
    });
    const d = await r.json();
    alert(d.posted ? '✅ Reminder posted!' : '❌ Failed to post.');
  };

  const postIneligibilityAlert = async () => {
    if (!window.confirm('Post keeper ineligibility alert to Discord?')) return;
    const r = await fetch(`${api}/api/lor/discord/keeper-ineligibility`, {
      method: 'POST', headers: { 'X-HQ-Pass': pass },
    });
    const d = await r.json();
    alert(d.posted ? '✅ Alert posted!' : '❌ Failed to post.');
  };

  const postKeeperRecsLive = async () => {
    if (!window.confirm('Post "Keeper Recommendations Now Live" to #league-of-record-intel?')) return;
    const r = await fetch(`${api}/api/lor/discord/keeper-recommendations-live`, {
      method: 'POST', headers: { 'X-HQ-Pass': pass },
    });
    const d = await r.json();
    alert(d.posted ? '✅ Announcement posted!' : '❌ Failed to post. Check Discord config.');
  };

  const postKeeperDeadlineValues = async () => {
    if (!window.confirm('Post top keeper surplus values (2-week deadline alert) to Discord?')) return;
    const r = await fetch(`${api}/api/lor/discord/keeper-deadline-values`, {
      method: 'POST', headers: { 'X-HQ-Pass': pass },
    });
    const d = await r.json();
    alert(d.posted ? '✅ Alert posted!' : '❌ Failed to post.');
  };

  const loadAllRecs = async () => {
    setAllRecsLoading(true);
    try {
      const r = await fetch(`${api}/api/lor/commissioner/all-recommendations`, {
        headers: { 'X-HQ-Pass': pass },
      });
      setAllRecs(r.ok ? await r.json() : null);
    } catch { setAllRecs(null); }
    setAllRecsLoading(false);
  };

  const saveSeason = async () => {
    setSeasonSaving(true);
    setSeasonMsg('');
    try {
      const r = await fetch(`${api}/api/lor/commissioner/season`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': pass },
        body: JSON.stringify({ ...seasonForm, season: parseInt(seasonForm.season) }),
      });
      if (r.ok) {
        setSeasonMsg({ type: 'success', text: 'Season saved.' });
        loadAll();
        setSeasonForm(f => ({ ...f, champion_name: '', runner_up_name: '', notes: '' }));
      } else {
        setSeasonMsg({ type: 'error', text: 'Save failed.' });
      }
    } catch {
      setSeasonMsg({ type: 'error', text: 'Error.' });
    }
    setSeasonSaving(false);
  };

  const deleteSeason = async (year) => {
    if (!window.confirm(`Delete ${year} season entry?`)) return;
    await fetch(`${api}/api/lor/commissioner/season/${year}`, {
      method: 'DELETE',
      headers: { 'X-HQ-Pass': pass },
    });
    loadAll();
  };

  const updateManager = async (uid, changes) => {
    await fetch(`${api}/api/lor/commissioner/manager/${uid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': pass },
      body: JSON.stringify(changes),
    });
    loadAll();
  };

  const loadLoreOverview = async () => {
    setLoreLoading(true);
    try {
      const r = await fetch(`${api}/api/lor/lore/overview`);
      setLoreOverview(r.ok ? await r.json() : null);
    } catch { setLoreOverview(null); }
    setLoreLoading(false);
  };

  const loadMockDrafts = async () => {
    setMockDraftsLoading(true);
    setMockDraftDetail(null);
    try {
      const r = await fetch(`${api}/api/lor/commissioner/mock-drafts`, { headers: { 'X-HQ-Pass': pass } });
      if (r.ok) { const d = await r.json(); setMockDrafts(d.drafts || []); }
    } catch { setMockDrafts([]); }
    setMockDraftsLoading(false);
  };

  const loadMockDraftDetail = async (id) => {
    setMockDraftsLoading(true);
    try {
      const r = await fetch(`${api}/api/lor/commissioner/mock-drafts/${id}`, { headers: { 'X-HQ-Pass': pass } });
      if (r.ok) setMockDraftDetail(await r.json());
    } catch {}
    setMockDraftsLoading(false);
  };

  const postLoreLaunch = async () => {
    if (!window.confirm('Post the Lore System launch announcement to #league-of-record-intel?')) return;
    const r = await fetch(`${api}/api/lor/discord/lore-launch`, {
      method: 'POST', headers: { 'X-HQ-Pass': pass },
    });
    const d = await r.json();
    alert(d.posted ? '✅ Lore launch announcement posted!' : '❌ Failed to post. Check Discord config.');
  };

  const generateAiNarrative = async (topic) => {
    setAiGenerating(true);
    setAiResult('');
    setLoreMsg('');
    try {
      const r = await fetch(`${api}/api/lor/lore/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': pass },
        body: JSON.stringify({ topic }),
      });
      const d = await r.json();
      if (r.ok) setAiResult(d.narrative || d.result || JSON.stringify(d));
      else setLoreMsg({ type: 'error', text: `AI generation failed: ${d.detail || 'Unknown error'}` });
    } catch { setLoreMsg({ type: 'error', text: 'Connection error during AI generation.' }); }
    setAiGenerating(false);
  };

  const postLaunch = async () => {
    if (!window.confirm('Post the League of Record launch announcement to Discord?')) return;
    const r = await fetch(`${api}/api/lor/discord/launch`, {
      method: 'POST',
      headers: { 'X-HQ-Pass': pass },
    });
    const d = await r.json();
    alert(d.posted ? '✅ Announcement posted!' : '❌ Failed to post. Check Discord config.');
  };

  if (!authed) {
    return (
      <div className="lor-auth-wall">
        <div className="lor-auth-card lor-card">
          <div className="lor-card-title">🔒 LOR Commissioner HQ</div>
          <p className="lor-auth-desc">Enter your commissioner password to continue.</p>
          <input
            type="password"
            className="lor-input"
            placeholder="Commissioner password"
            value={pass}
            onChange={e => setPass(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doAuth()}
          />
          {authError && <div className="lor-auth-error">{authError}</div>}
          <button className="lor-btn lor-btn--primary" onClick={doAuth}>
            Unlock Commissioner HQ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lor-commissioner">
      <div className="lor-page-header">
        <h2 className="lor-page-title"><Icon emoji="🔒" size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} /> LOR Commissioner HQ</h2>
        <button className="lor-btn lor-btn--secondary lor-btn--sm" onClick={postLaunch}>
          <Icon emoji="📣" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Post Launch Announcement
        </button>
      </div>

      <div className="lor-comm-tabs">
        {[
          ['seasons',    '📜 Season Archive'],
          ['keepers',    '🔑 Keeper HQ'],
          ['managers',   '👥 Managers'],
          ['mock-drafts','🏈 Mock Drafts'],
          ['lore',       '📖 Lore'],
          ['audit',      '📋 Audit Log'],
        ].map(([key, label]) => (
          <button
            key={key}
            className={`lor-comm-tab${tab === key ? ' active' : ''}`}
            onClick={() => {
              setTab(key);
              if (key === 'keepers') loadKeeperData();
              if (key === 'lore') loadLoreOverview();
              if (key === 'mock-drafts') loadMockDrafts();
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'seasons' && (
        <div>
          <div className="lor-card" style={{ marginBottom: 24 }}>
            <div className="lor-card-title">Enter Season Data</div>
            <div className="lor-form-grid">
              <label className="lor-label">
                Season Year
                <input
                  type="number"
                  className="lor-input"
                  value={seasonForm.season}
                  onChange={e => setSeasonForm(f => ({ ...f, season: e.target.value }))}
                  min="2012" max="2030"
                />
              </label>
              <label className="lor-label">
                Champion
                <input
                  type="text"
                  className="lor-input"
                  placeholder="Manager display name"
                  value={seasonForm.champion_name}
                  onChange={e => setSeasonForm(f => ({ ...f, champion_name: e.target.value }))}
                />
              </label>
              <label className="lor-label">
                Runner-up
                <input
                  type="text"
                  className="lor-input"
                  placeholder="Manager display name"
                  value={seasonForm.runner_up_name}
                  onChange={e => setSeasonForm(f => ({ ...f, runner_up_name: e.target.value }))}
                />
              </label>
              <label className="lor-label">
                Source
                <select
                  className="lor-input"
                  value={seasonForm.source}
                  onChange={e => setSeasonForm(f => ({ ...f, source: e.target.value }))}
                >
                  <option value="manual">Manual entry</option>
                  <option value="sleeper">Sleeper</option>
                  <option value="records">Historical records</option>
                </select>
              </label>
              <label className="lor-label" style={{ gridColumn: '1 / -1' }}>
                Notes
                <textarea
                  className="lor-input lor-textarea"
                  placeholder="Notable moments, corrections, etc."
                  value={seasonForm.notes}
                  onChange={e => setSeasonForm(f => ({ ...f, notes: e.target.value }))}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
              <button className="lor-btn lor-btn--primary" onClick={saveSeason} disabled={seasonSaving}>
                {seasonSaving ? 'Saving...' : 'Save Season'}
              </button>
              {seasonMsg && <span className="lor-form-msg"><Icon emoji={seasonMsg.type === 'success' ? '✅' : '❌'} size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{seasonMsg.text}</span>}
            </div>
          </div>

          <div className="lor-card">
            <div className="lor-card-title">Archived Seasons ({seasons.length})</div>
            {seasons.length === 0 ? (
              <div className="lor-empty">No seasons entered yet.</div>
            ) : (
              <table className="lor-table">
                <thead>
                  <tr><th>Year</th><th>Champion</th><th>Runner-up</th><th>Source</th><th>Notes</th><th></th></tr>
                </thead>
                <tbody>
                  {seasons.map(s => (
                    <tr key={s.season}>
                      <td><strong>{s.season}</strong></td>
                      <td>{s.champion_name || '—'}</td>
                      <td>{s.runner_up_name || '—'}</td>
                      <td><span className={`lor-source-badge lor-source-badge--${s.source}`}>{s.source}</span></td>
                      <td className="lor-notes-cell">{s.notes || '—'}</td>
                      <td>
                        <button className="lor-btn lor-btn--danger lor-btn--sm" onClick={() => deleteSeason(s.season)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'managers' && (
        <div className="lor-card">
          <div className="lor-card-title">Manager Profiles ({managers.length})</div>
          {managers.length === 0 ? (
            <div className="lor-empty">No managers loaded yet. Wait for first data refresh.</div>
          ) : (
            <table className="lor-table">
              <thead>
                <tr>
                  <th>Manager</th>
                  <th>Visibility</th>
                  <th>Former?</th>
                  <th>LOR Roster</th>
                  <th>Dynasty Roster</th>
                </tr>
              </thead>
              <tbody>
                {managers.map(m => (
                  <tr key={m.sleeper_user_id} className={m.is_former ? 'lor-row--former' : ''}>
                    <td>
                      <div className="lor-manager-name">{m.display_name}</div>
                      <div className="lor-team-name">@{m.username}</div>
                    </td>
                    <td>
                      <select
                        className="lor-input lor-input--sm"
                        value={m.league_visibility}
                        onChange={e => updateManager(m.sleeper_user_id, { league_visibility: e.target.value })}
                      >
                        <option value="both">Both leagues</option>
                        <option value="dynasty_only">Dynasty only</option>
                        <option value="lor_only">LOR only</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={m.is_former}
                        onChange={e => updateManager(m.sleeper_user_id, { is_former: e.target.checked })}
                      />
                    </td>
                    <td>{m.lor_roster_id ?? '—'}</td>
                    <td>{m.dynasty_roster_id ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'keepers' && (
        <div>
          {keeperMsg && <div className="lor-form-msg" style={{ marginBottom: 16 }}><Icon emoji={keeperMsg.type === 'success' ? '✅' : '❌'} size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{keeperMsg.text}</div>}

          {/* Discord controls */}
          <div className="lor-card" style={{ marginBottom: 20 }}>
            <div className="lor-card-title" style={{ marginBottom: 12 }}><Icon emoji="📣" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Discord Alerts</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="lor-btn lor-btn--primary lor-btn--sm" onClick={postKeeperRecsLive}>
                <Icon emoji="🏆" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Recs Now Live
              </button>
              <button className="lor-btn lor-btn--secondary lor-btn--sm" onClick={postKeeperDeadlineValues}>
                <Icon emoji="📊" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Top Values (2wk)
              </button>
              <button className="lor-btn lor-btn--secondary lor-btn--sm" onClick={() => postDeadlineReminder(14)}>
                <Icon emoji="📅" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> 14-Day Reminder
              </button>
              <button className="lor-btn lor-btn--secondary lor-btn--sm" onClick={() => postDeadlineReminder(7)}>
                <Icon emoji="⚠️" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> 7-Day Reminder
              </button>
              <button className="lor-btn lor-btn--secondary lor-btn--sm" onClick={() => postDeadlineReminder(3)}>
                🚨 3-Day Reminder
              </button>
              <button className="lor-btn lor-btn--primary lor-btn--sm" onClick={postKeeperBoard}>
                <Icon emoji="🏆" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Post Full Board
              </button>
              <button className="lor-btn lor-btn--secondary lor-btn--sm" onClick={postIneligibilityAlert}>
                🚫 Ineligibility Alert
              </button>
            </div>
          </div>

          {/* Declaration tracker */}
          <div className="lor-card" style={{ marginBottom: 20 }}>
            <div className="lor-card-title" style={{ marginBottom: 12 }}>
              <Icon emoji="📋" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Declaration Status
              {keeperBoard && (
                <span style={{ marginLeft: 10, fontSize: '0.8rem', color: 'var(--text3)', fontWeight: 400 }}>
                  {keeperBoard.declared_count}/{keeperBoard.total_managers} declared
                </span>
              )}
            </div>
            {!keeperBoard ? (
              <div className="lor-empty">Click "Keeper HQ" tab to load data.</div>
            ) : (
              <table className="lor-table">
                <thead>
                  <tr><th>Manager</th><th>Declared</th><th>Keepers</th><th>HQ Status</th><th></th></tr>
                </thead>
                <tbody>
                  {(keeperBoard.managers || []).map(m => (
                    <tr key={m.roster_id}>
                      <td><div className="lor-manager-name">{m.manager_name}</div></td>
                      <td>
                        {m.has_declared
                          ? <span style={{ color: 'var(--green)', fontWeight: 600 }}>✓ {m.declared_count} declared</span>
                          : <span style={{ color: 'var(--text3)' }}>Pending</span>}
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>
                        {m.declared_details.map(d => `${d.player_name} (R${d.keeper_cost_round})`).join(' · ') || '—'}
                      </td>
                      <td>
                        {m.hq_received
                          ? <span style={{ color: 'var(--green)', fontSize: '0.78rem' }}>Received ✓</span>
                          : <span style={{ color: 'var(--text3)', fontSize: '0.78rem' }}>Not confirmed</span>}
                      </td>
                      <td>
                        {!m.hq_received && (
                          <button
                            className="lor-btn lor-btn--primary lor-btn--sm"
                            onClick={() => markDeclarationReceived(m.roster_id, m.manager_name)}
                          >
                            Mark Received
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ADP overrides */}
          <div className="lor-card" style={{ marginBottom: 20 }}>
            <div className="lor-card-title" style={{ marginBottom: 12 }}>📐 ADP Override</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text3)', marginBottom: 14 }}>
              Override the ADP estimate for a specific player. This affects surplus value calculations for all managers.
              Player ID is from Sleeper (visible in the keeper analysis URL or roster data).
            </p>
            <div className="lor-form-grid">
              <label className="lor-label">Player ID (Sleeper)
                <input className="lor-input" placeholder="e.g. 4984" value={adpForm.player_id}
                  onChange={e => setAdpForm(f => ({ ...f, player_id: e.target.value }))} />
              </label>
              <label className="lor-label">Player Name
                <input className="lor-input" placeholder="e.g. Josh Allen" value={adpForm.player_name}
                  onChange={e => setAdpForm(f => ({ ...f, player_name: e.target.value }))} />
              </label>
              <label className="lor-label">ADP Round (1–20)
                <input className="lor-input" type="number" min="1" max="20" placeholder="e.g. 2"
                  value={adpForm.adp_round}
                  onChange={e => setAdpForm(f => ({ ...f, adp_round: e.target.value }))} />
              </label>
              <label className="lor-label">Notes
                <input className="lor-input" placeholder="Optional" value={adpForm.notes}
                  onChange={e => setAdpForm(f => ({ ...f, notes: e.target.value }))} />
              </label>
            </div>
            <button className="lor-btn lor-btn--primary" style={{ marginTop: 12 }} onClick={saveAdpOverride}>
              Save ADP Override
            </button>
            {keeperAdpList.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text3)', marginBottom: 8 }}>Existing overrides:</div>
                {keeperAdpList.map(a => (
                  <div key={a.player_id} style={{ fontSize: '0.8rem', color: 'var(--text3)', marginBottom: 4 }}>
                    {a.player_name} ({a.player_id}) → Round {a.adp_round}{a.notes ? ` — ${a.notes}` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* All Recommendations */}
          <div className="lor-card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="lor-card-title"><Icon emoji="🏆" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> All Manager Recommendations</div>
              <button className="lor-btn lor-btn--secondary lor-btn--sm" onClick={loadAllRecs} disabled={allRecsLoading}>
                {allRecsLoading ? 'Loading…' : allRecs ? 'Refresh' : 'Load'}
              </button>
            </div>
            {!allRecs && !allRecsLoading && (
              <div className="lor-empty" style={{ padding: '12px 0' }}>
                Click Load to see all managers' keeper recommendations.
              </div>
            )}
            {allRecsLoading && <div className="lor-loading" style={{ padding: '12px 0' }}>Loading recommendations…</div>}
            {allRecs && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {allRecs.map(m => {
                  const top2 = m.top_2_recommendation || [];
                  const keeps = m.keep_count || 0;
                  const considers = m.consider_count || 0;
                  return (
                    <div key={m.roster_id} style={{
                      padding: '10px 14px', borderRadius: 8,
                      background: keeps >= 2 ? 'rgba(76,175,80,0.03)' : '#111',
                      border: `1px solid ${keeps >= 2 ? 'rgba(76,175,80,0.15)' : 'var(--border)'}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontWeight: 700, color: 'var(--text2)', fontSize: '0.88rem' }}>{m.manager_name}</span>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>
                          {keeps} KEEP · {considers} CONSIDER
                        </span>
                        {m.no_options_warning && (
                          <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--red)' }}>
                            <Icon emoji="⚠️" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> No strong options
                          </span>
                        )}
                      </div>
                      {top2.length === 0 ? (
                        <div style={{ fontSize: '0.78rem', color: 'var(--text3)' }}>No eligible candidates found.</div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {top2.map((k, i) => {
                            const recColor = k.recommendation?.cls === 'keep' ? 'var(--green)'
                              : k.recommendation?.cls === 'consider' ? '#FF9F1C' : '#888';
                            return (
                              <div key={k.player_id} style={{
                                flex: '1 1 160px', padding: '6px 10px', borderRadius: 6,
                                background: 'var(--bg3)', border: `1px solid ${recColor}30`,
                              }}>
                                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '0.82rem' }}>
                                  #{i + 1} {k.player_name}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text3)', marginBottom: 3 }}>
                                  {k.position} · {k.team}
                                </div>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text3)', marginBottom: 4 }}>
                                  {k.full_math}
                                </div>
                                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                  <span style={{
                                    fontSize: '0.65rem', fontWeight: 700, color: recColor,
                                    background: `${recColor}15`, padding: '1px 6px', borderRadius: 8,
                                    border: `1px solid ${recColor}40`,
                                  }}>
                                    {k.recommendation?.label}
                                  </span>
                                  <span style={{ fontSize: '0.65rem', color: 'var(--text3)' }}>{k.risk_level} Risk</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {m.tradeoff_analysis && (
                        <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--text3)', lineHeight: 1.4 }}>
                          <Icon emoji="⚖️" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {m.tradeoff_analysis}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Eligibility overrides */}
          <div className="lor-card">
            <div className="lor-card-title" style={{ marginBottom: 12 }}><Icon emoji="⚖️" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Eligibility Override</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text3)', marginBottom: 14 }}>
              Correct keeper source data or block eligibility. Round 1 ineligibility cannot be overridden.
            </p>
            <div className="lor-form-grid">
              <label className="lor-label">Player ID (Sleeper)
                <input className="lor-input" placeholder="e.g. 4984" value={overrideForm.player_id}
                  onChange={e => setOverrideForm(f => ({ ...f, player_id: e.target.value }))} />
              </label>
              <label className="lor-label">Player Name
                <input className="lor-input" placeholder="e.g. Joe Burrow" value={overrideForm.player_name}
                  onChange={e => setOverrideForm(f => ({ ...f, player_name: e.target.value }))} />
              </label>
              <label className="lor-label">Override Status
                <select className="lor-input" value={overrideForm.eligible}
                  onChange={e => setOverrideForm(f => ({ ...f, eligible: e.target.value === 'true' }))}>
                  <option value="true">Eligible (restore)</option>
                  <option value="false">Ineligible (block)</option>
                </select>
              </label>
              <label className="lor-label">Corrected Original Round (optional)
                <input className="lor-input" type="number" min="1" placeholder="Only for a source-data correction"
                  value={overrideForm.corrected_draft_round}
                  onChange={e => setOverrideForm(f => ({ ...f, corrected_draft_round: e.target.value }))} />
              </label>
              <label className="lor-label" style={{ gridColumn: '1 / -1' }}>Reason (required)
                <textarea className="lor-input lor-textarea" placeholder="e.g. Commissioner ruling — eligible per league vote"
                  value={overrideForm.reason}
                  onChange={e => setOverrideForm(f => ({ ...f, reason: e.target.value }))} />
              </label>
            </div>
            <button className="lor-btn lor-btn--primary" style={{ marginTop: 12 }} onClick={saveEligibilityOverride}>
              Save Override
            </button>
            {keeperOverrides.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text3)', marginBottom: 8 }}>Active overrides:</div>
                {keeperOverrides.map(o => (
                  <div key={o.player_id} style={{ fontSize: '0.8rem', marginBottom: 6 }}>
                    <span style={{ color: o.eligible ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                      {o.eligible ? 'ELIGIBLE' : 'INELIGIBLE'}
                    </span>
                    {' '}<span style={{ color: 'var(--text2)' }}>{o.player_name}</span>
                    {o.corrected_draft_round && <span style={{ color: 'var(--lor-accent)' }}> · corrected to R{o.corrected_draft_round}</span>}
                    {' '}<span style={{ color: 'var(--text3)' }}>— {o.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'lore' && (
        <div>
          {loreMsg && <div className="lor-form-msg" style={{ marginBottom: 16 }}><Icon emoji={loreMsg.type === 'success' ? '✅' : '❌'} size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{loreMsg.text}</div>}

          {/* Discord */}
          <div className="lor-card" style={{ marginBottom: 20 }}>
            <div className="lor-card-title" style={{ marginBottom: 12 }}><Icon emoji="📣" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Lore Announcements</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="lor-btn lor-btn--primary lor-btn--sm" onClick={postLoreLaunch}>
                📜 Post Lore Launch
              </button>
            </div>
          </div>

          {/* AI Narrative Generator */}
          <div className="lor-card" style={{ marginBottom: 20 }}>
            <div className="lor-card-title" style={{ marginBottom: 8 }}><Icon emoji="🤖" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> AI Narrative Generator</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text3)', marginBottom: 14 }}>
              Generate narrative text for any lore topic. Topics: <code>the_trade</code>, <code>commissioner_legacy</code>,
              <code>era_foundation</code>, <code>era_dominance</code>, <code>founding_members</code>
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {['the_trade', 'commissioner_legacy', 'era_foundation', 'era_dominance', 'founding_members'].map(topic => (
                <button
                  key={topic}
                  className="lor-btn lor-btn--secondary lor-btn--sm"
                  disabled={aiGenerating}
                  onClick={() => generateAiNarrative(topic)}
                >
                  {topic.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
            {aiGenerating && <div className="lor-loading" style={{ marginTop: 12 }}>Generating narrative…</div>}
            {aiResult && (
              <div style={{
                marginTop: 14, padding: 14, background: 'var(--bg2)', borderRadius: 8,
                border: '1px solid #2a2a2a', fontSize: '0.82rem', color: 'var(--text2)',
                lineHeight: 1.6, whiteSpace: 'pre-wrap',
              }}>
                {aiResult}
              </div>
            )}
          </div>

          {/* Lore Overview Preview */}
          <div className="lor-card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="lor-card-title">📖 Lore Overview Preview</div>
              <button className="lor-btn lor-btn--secondary lor-btn--sm" onClick={loadLoreOverview} disabled={loreLoading}>
                {loreLoading ? 'Loading…' : loreOverview ? 'Refresh' : 'Load'}
              </button>
            </div>
            {loreLoading && <div className="lor-loading">Loading lore data…</div>}
            {!loreOverview && !loreLoading && (
              <div className="lor-empty">Click Load to preview lore content.</div>
            )}
            {loreOverview && (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Eras ({(loreOverview.eras || []).length})
                  </div>
                  {(loreOverview.eras || []).map((era, i) => (
                    <div key={i} style={{ fontSize: '0.82rem', color: 'var(--text2)', marginBottom: 4, paddingLeft: 10, borderLeft: '2px solid #333' }}>
                      <span style={{ color: 'var(--text)', fontWeight: 600 }}>{era.era_name || era.name}</span>
                      {' '}<span style={{ color: 'var(--text3)' }}>{era.year_range || era.years}</span>
                      {' '}<span style={{ color: 'var(--text3)' }}>— {era.era_subtitle || era.subtitle}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Milestones ({(loreOverview.milestones || []).length})
                  </div>
                  {(loreOverview.milestones || []).slice(0, 5).map((m, i) => (
                    <div key={i} style={{ fontSize: '0.82rem', color: 'var(--text2)', marginBottom: 4, paddingLeft: 10, borderLeft: '2px solid #333' }}>
                      <span style={{ color: 'var(--text)' }}>{m.icon} {m.title}</span>
                      {' '}<span style={{ color: 'var(--text3)' }}>{m.year}</span>
                    </div>
                  ))}
                  {(loreOverview.milestones || []).length > 5 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text3)', paddingLeft: 10 }}>
                      + {(loreOverview.milestones || []).length - 5} more…
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 4 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Founding Members ({(loreOverview.founding_members || []).length})
                  </div>
                  {(loreOverview.founding_members || []).map((m, i) => (
                    <div key={i} style={{ fontSize: '0.82rem', color: 'var(--text2)', marginBottom: 4, paddingLeft: 10, borderLeft: '2px solid #333' }}>
                      <span style={{ color: 'var(--text)', fontWeight: 600 }}>{m.display_name}</span>
                      {m.isCommissioner && <span style={{ color: 'var(--lor-accent)', marginLeft: 6 }}>👑</span>}
                      {' '}<span style={{ color: 'var(--text3)' }}>est. {m.original_team_name || m.original_2012_team}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quick links */}
          <div className="lor-card">
            <div className="lor-card-title" style={{ marginBottom: 12 }}>🔗 Public Lore Pages</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                ['Timeline', '/league-of-record/lore'],
                ['The Trade', '/league-of-record/the-trade'],
                ['Commissioner Legacy', '/league-of-record/commissioner-legacy'],
                ['Founding Members', '/league-of-record/founding-members'],
                ['Milestones', '/league-of-record/milestones'],
              ].map(([label, path]) => (
                <a
                  key={path}
                  href={path}
                  className="lor-btn lor-btn--secondary lor-btn--sm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {label} ↗
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'mock-drafts' && (
        <div className="lor-card">
          <div className="lor-card-title">Mock Draft Review ({mockDrafts.length} drafts)</div>
          {mockDraftsLoading && <div className="lor-empty">Loading…</div>}
          {!mockDraftsLoading && mockDraftDetail && (
            <div>
              <button className="lor-btn-secondary" style={{ marginBottom: 12 }}
                onClick={() => setMockDraftDetail(null)}>← Back to all drafts</button>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                {mockDraftDetail.display_name} — {mockDraftDetail.mode} mode
              </div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                {mockDraftDetail.completed_at ? new Date(mockDraftDetail.completed_at).toLocaleString() : '—'}
                {' · '}Overall Grade: <strong>{mockDraftDetail.overall_grade || '?'}</strong>
              </div>
              {mockDraftDetail.strategy_summary && (
                <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, lineHeight: 1.6 }}>
                  {mockDraftDetail.strategy_summary}
                </div>
              )}
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: '#AAA' }}>Picks</div>
              <table className="lor-table">
                <thead>
                  <tr><th>Pick</th><th>Pos</th><th>Player</th><th>ADP</th><th>Grade</th><th>Rec?</th><th>Should Have Done</th></tr>
                </thead>
                <tbody>
                  {((mockDraftDetail.analysis || {}).picks_graded || mockDraftDetail.picks || []).map((p, i) => (
                    <tr key={i}>
                      <td>{p.pick_display || `R${p.round}`}</td>
                      <td>{p.position}</td>
                      <td>{p.player_name}</td>
                      <td>{p.adp_pick_no ? `#${Math.round(p.adp_pick_no)}` : '—'}</td>
                      <td style={{ color: p.grade === 'value' ? 'var(--green)' : p.grade === 'reach' ? 'var(--red)' : '#AAA' }}>
                        {p.grade || '—'}
                        {p.delta !== null && p.delta !== undefined ? ` (${p.delta > 0 ? '+' : ''}${p.delta})` : ''}
                      </td>
                      <td>{p.was_recommended ? '✓' : '—'}</td>
                      <td style={{ fontSize: 11, color: '#AAA', maxWidth: 240 }}>{p.should_have_done || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!mockDraftsLoading && !mockDraftDetail && (
            <>
              <div style={{ marginBottom: 12 }}>
                <input
                  className="lor-input"
                  placeholder="Filter by manager name…"
                  value={mockFilter}
                  onChange={e => setMockFilter(e.target.value)}
                  style={{ maxWidth: 260 }}
                />
              </div>
              {mockDrafts.length === 0 ? (
                <div className="lor-empty">No mock drafts completed yet.</div>
              ) : (
                <table className="lor-table">
                  <thead>
                    <tr><th>Manager</th><th>Date</th><th>Mode</th><th>Slot</th><th>Grade</th><th>Best Pick</th><th>Biggest Reach</th><th></th></tr>
                  </thead>
                  <tbody>
                    {mockDrafts
                      .filter(d => !mockFilter || (d.display_name || '').toLowerCase().includes(mockFilter.toLowerCase()))
                      .map(d => (
                        <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => loadMockDraftDetail(d.id)}>
                          <td>{d.display_name}</td>
                          <td className="lor-timestamp">{d.completed_at ? new Date(d.completed_at).toLocaleDateString() : '—'}</td>
                          <td>{d.mode}</td>
                          <td>{d.draft_slot || '—'}</td>
                          <td style={{ fontWeight: 800, color: d.overall_grade === 'A' ? 'var(--green)' : d.overall_grade === 'B' ? '#3498DB' : d.overall_grade === 'C' ? '#F39C12' : 'var(--red)' }}>
                            {d.overall_grade || '?'}
                          </td>
                          <td>{d.best_value?.player_name || '—'}</td>
                          <td>{d.biggest_reach?.player_name || '—'}</td>
                          <td style={{ color: 'var(--lor-gold)' }}>›</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'audit' && (
        <div className="lor-card">
          <div className="lor-card-title">LOR Audit Log ({auditLog.length} events)</div>
          {auditLog.length === 0 ? (
            <div className="lor-empty">No LOR audit events yet.</div>
          ) : (
            <table className="lor-table">
              <thead>
                <tr><th>Time</th><th>Event</th><th>Entity</th><th>Notes</th></tr>
              </thead>
              <tbody>
                {auditLog.map((e, i) => (
                  <tr key={i}>
                    <td className="lor-timestamp">
                      {new Date(e.timestamp).toLocaleString()}
                    </td>
                    <td><code>{e.event_type}</code></td>
                    <td>{e.entity_id}</td>
                    <td className="lor-notes-cell">{e.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
